import { useState, useEffect, useCallback } from 'react'
import { M365User, AvailableLicense, getLicenseDisplayName } from '../../types/m365Users'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface LicensesSectionProps {
  user: M365User
  customerId: string
  customerTenantId: string
  onLicenseUpdated: () => void
}

export function LicensesSection({
  user,
  customerId,
  customerTenantId,
  onLicenseUpdated,
}: LicensesSectionProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [availableLicenses, setAvailableLicenses] = useState<AvailableLicense[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Track which licenses to add/remove
  const [selectedToAdd, setSelectedToAdd] = useState<string[]>([])
  const [selectedToRemove, setSelectedToRemove] = useState<string[]>([])

  const userLicenseIds = user.assignedLicenses?.map(l => l.skuId) || []

  const fetchAvailableLicenses = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const token = await getCustomerGraphAccessToken(customerTenantId)
      const response = await api.getM365AvailableLicenses(token, customerId) as { licenses: AvailableLicense[] }
      setAvailableLicenses(response.licenses || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load available licenses')
    } finally {
      setLoading(false)
    }
  }, [customerId, customerTenantId, getCustomerGraphAccessToken])

  useEffect(() => {
    fetchAvailableLicenses()
  }, [fetchAvailableLicenses])

  const handleAddLicense = (skuId: string) => {
    if (selectedToRemove.includes(skuId)) {
      setSelectedToRemove(prev => prev.filter(id => id !== skuId))
    } else {
      setSelectedToAdd(prev => [...prev, skuId])
    }
  }

  const handleRemoveLicense = (skuId: string) => {
    if (selectedToAdd.includes(skuId)) {
      setSelectedToAdd(prev => prev.filter(id => id !== skuId))
    } else {
      setSelectedToRemove(prev => [...prev, skuId])
    }
  }

  const handleCancelSelection = (skuId: string) => {
    setSelectedToAdd(prev => prev.filter(id => id !== skuId))
    setSelectedToRemove(prev => prev.filter(id => id !== skuId))
  }

  const handleSaveChanges = async () => {
    if (selectedToAdd.length === 0 && selectedToRemove.length === 0) return

    try {
      setSaving(true)
      setError(null)

      const token = await getCustomerGraphAccessToken(customerTenantId)

      await api.assignM365UserLicense(token, customerId, user.id, {
        addLicenses: selectedToAdd.map(skuId => ({ skuId })),
        removeLicenses: selectedToRemove,
      })

      setSelectedToAdd([])
      setSelectedToRemove([])
      setSuccess('License changes saved successfully')
      setTimeout(() => setSuccess(null), 3000)
      onLicenseUpdated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save license changes')
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = selectedToAdd.length > 0 || selectedToRemove.length > 0

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span className="ml-2 text-sm text-gray-500">Loading licenses...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            License Management
          </h3>
          {hasChanges && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => { setSelectedToAdd([]); setSelectedToRemove([]); }}
                disabled={saving}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={saving}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  `Save Changes (${selectedToAdd.length + selectedToRemove.length})`
                )}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-700">{success}</p>
          </div>
        )}

        {/* Current Licenses */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Assigned Licenses ({userLicenseIds.length})</h4>
          {userLicenseIds.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No licenses assigned</p>
          ) : (
            <div className="space-y-2">
              {userLicenseIds.map(skuId => {
                const license = availableLicenses.find(l => l.skuId === skuId)
                const displayName = license ? getLicenseDisplayName(license.skuPartNumber) : skuId
                const isMarkedForRemoval = selectedToRemove.includes(skuId)

                return (
                  <div
                    key={skuId}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isMarkedForRemoval
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                        isMarkedForRemoval
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {isMarkedForRemoval ? 'To Remove' : 'Assigned'}
                      </span>
                      <span className="ml-3 text-sm font-medium text-gray-900">{displayName}</span>
                      {license && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({license.consumedUnits} / {license.totalUnits} used)
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => isMarkedForRemoval ? handleCancelSelection(skuId) : handleRemoveLicense(skuId)}
                      className={`text-sm font-medium ${
                        isMarkedForRemoval
                          ? 'text-gray-600 hover:text-gray-800'
                          : 'text-red-600 hover:text-red-800'
                      }`}
                    >
                      {isMarkedForRemoval ? 'Undo' : 'Remove'}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Available Licenses */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Available Licenses</h4>
          {availableLicenses.filter(l => !userLicenseIds.includes(l.skuId)).length === 0 ? (
            <p className="text-sm text-gray-500 italic">No additional licenses available</p>
          ) : (
            <div className="space-y-2">
              {availableLicenses
                .filter(license => !userLicenseIds.includes(license.skuId))
                .map(license => {
                  const displayName = getLicenseDisplayName(license.skuPartNumber)
                  const isMarkedForAdd = selectedToAdd.includes(license.skuId)
                  const hasAvailable = license.availableUnits > 0

                  return (
                    <div
                      key={license.skuId}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        isMarkedForAdd
                          ? 'border-green-300 bg-green-50'
                          : hasAvailable
                          ? 'border-gray-200 bg-white hover:bg-gray-50'
                          : 'border-gray-200 bg-gray-100 opacity-60'
                      }`}
                    >
                      <div className="flex items-center">
                        {isMarkedForAdd && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mr-3">
                            To Add
                          </span>
                        )}
                        <span className="text-sm font-medium text-gray-900">{displayName}</span>
                        <span className={`ml-2 text-xs ${hasAvailable ? 'text-gray-500' : 'text-red-500'}`}>
                          ({license.availableUnits} / {license.totalUnits} available)
                        </span>
                      </div>
                      {hasAvailable && (
                        <button
                          onClick={() => isMarkedForAdd ? handleCancelSelection(license.skuId) : handleAddLicense(license.skuId)}
                          className={`text-sm font-medium ${
                            isMarkedForAdd
                              ? 'text-gray-600 hover:text-gray-800'
                              : 'text-primary-600 hover:text-primary-800'
                          }`}
                        >
                          {isMarkedForAdd ? 'Undo' : 'Add'}
                        </button>
                      )}
                    </div>
                  )
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
